import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { InvoiceCycleProcessingModel } from '../../../../api/Argentia/endpoints/invoiceCyclesExecutions/model/cfExecutionModel'
import { EnumEstadoCiclo } from '../../../../api/oData/enums/enumEstadoCiclo'
import { FloatingNotification } from '../../../../components/shared/floatingNotification/floatingNotification'
import { NonAuthRoutes } from '../../../../model'
import {
  removeActiveProcess,
  updateProcessStatus
} from '../../../../redux/actions/processing.actions'
import { useInvoiceCyclePolling } from '../../hooks/useInvoiceCyclePolling'
import {
  fetchInvoiceCycleExecution,
  fetchInvoiceCycleProcessing
} from '../../invoiceCycles.service'
import { invoiceCycleStatusConfig, resolveNavigateTo } from '../invoiceCycleStatusConfig'
import { InvoiceCycleProcessingProgress } from './invoiceCycleProcessingProgress'

interface FloatingProcessingProgressProps {
  processId: string
  executionCycleId: number
  currentStatus: EnumEstadoCiclo
  additionalState?: any
  cycleName?: string
}

/**
 * Componente de progreso de procesamiento en formato de notificación flotante
 *
 * Maneja toda la lógica de polling y actualización de estado para invoice cycles
 */
export const FloatingProcessingProgress: React.FC<FloatingProcessingProgressProps> = ({
  processId,
  executionCycleId,
  currentStatus,
  additionalState,
  cycleName
}) => {
  const dispatch = useDispatch()
  const history = useHistory()

  const [processingProgress, setProcessingProgress] = useState<InvoiceCycleProcessingModel>({
    processedElements: 0,
    totalProcessingElements: 0
  })

  /**
   * Función que consulta el estado actual y el progreso en paralelo, actualizando Redux
   */
  const fetchCycleStatus = async (): Promise<EnumEstadoCiclo> => {
    try {
      // Ejecutar ambas llamadas en paralelo
      const [executionResponse, progressResponse] = await Promise.all([
        fetchInvoiceCycleExecution(executionCycleId),
        fetchInvoiceCycleProcessing(executionCycleId)
      ])

      if (executionResponse.success && executionResponse.data) {
        const newStatus = executionResponse.data.status as EnumEstadoCiclo

        // Actualizar Redux
        dispatch(updateProcessStatus(processId, newStatus))

        // Actualizar progreso si está disponible
        if (progressResponse.success && progressResponse.data) {
          setProcessingProgress(progressResponse.data)
        }

        return newStatus
      }
      return currentStatus
    } catch (error) {
      console.error('Error fetching cycle status:', error)
      throw error
    }
  }

  const { pollingState, resumePolling, cancelPolling, nextStatus, navigateToNext } =
    useInvoiceCyclePolling(
      executionCycleId,
      currentStatus,
      fetchCycleStatus,
      additionalState,
      additionalState?.originState, // Estado de origen para evitar race conditions
      true // disableAutoNavigation - NO navegar automáticamente, mostrar botón al usuario
    )

  const statusConfig = invoiceCycleStatusConfig[currentStatus]
  // isCompleted cuando ya no está procesando y hay un nextStatus disponible
  const isCompleted = !statusConfig.isProcessing

  const handleBack = () => {
    // Navegar de vuelta a la página de listado
    history.push(NonAuthRoutes.invoiceCyclesNew)
    // Remover del Redux porque ahora está en la página
    dispatch(removeActiveProcess(processId))
    handleClose()
  }

  const handleClose = () => {
    cancelPolling()
    dispatch(removeActiveProcess(processId))
  }
  console.log(statusConfig, pollingState, isCompleted)

  return (
    <FloatingNotification
      title={cycleName || `Ciclo #${executionCycleId}`}
      allowClose={true}
      onClose={handleClose}
    >
      <InvoiceCycleProcessingProgress
        isProcessing={statusConfig.isProcessing}
        pollingState={pollingState}
        onResume={resumePolling}
        onBack={handleBack}
        isCompleted={isCompleted}
        onNavigate={() => {
          const config = invoiceCycleStatusConfig[currentStatus]
          const route = resolveNavigateTo(config.navigateTo, additionalState)
          if (route) {
            history.push(route, { executionCycleId, ...additionalState })
          }
          handleClose()
        }}
        processedElements={processingProgress.processedElements}
        totalProcessingElements={processingProgress.totalProcessingElements}
        variant="floating"
      />
    </FloatingNotification>
  )
}
